import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import useRequest from "../../../components/hooks/use-request";
import Navbar from "../../../components/layout/navbar";
import Icon from "../../../assets/icons";
import Tab from "../../../components/tab";
import { showToast } from "../../../components/toast";
import { I_Profile } from "../../inventory/types";
import { capitalizeFirstLetter } from "../../../utilz/function";
import { RingLoader } from "react-spinners";
import ActionModal from "./action-modal";
import AddSupplierDialog from "../../../components/suppliers/add-supplier-dialog";
import Button from "../../../components/button";

interface SupplierData {
  _id: string;
  name: string;
  email: string;
  contactPerson: string;
  phone: number;
  status: string;
  supplierId:string;
  supplierType: string;
  businessRegNumber: number;
}

const SupplierDetails = () => {
  const [supplier, setSupplier] = useState<SupplierData>();
  const { id } = useParams<{ id: string }>();
  const [supplierModal, setSupplierModal] = useState(false);
  const navigate = useNavigate();
  const [activeProduct, setActiveProduct] = useState("Products");
  const [modalConfig, setModalConfig] = useState<{
    isVisible: boolean;
    actionType: "approve" | "delete";
  }>({
    isVisible: false,
    actionType: "approve",
  });

  const [profile, setProfile] = useState<I_Profile | null>(null);
  const userToken = localStorage.getItem("token");
  const { makeRequest: getProfile } = useRequest("/users/profile", "GET", {
    Authorization: `Bearer ${userToken}`,
  });
  const { makeRequest: getSupplierById } = useRequest(
    `/suppliers/${id}`,
    "GET"
  );

  const { makeRequest: deleteSupplier } = useRequest(
    `/suppliers/${id}`,
    "DELETE"
  );

  const { makeRequest: approveSupplier, loading } = useRequest(
    `/suppliers/${id}/approve`,
    "POST",
    {
      Authorization: `Bearer ${userToken}`,
    }
  );

  useEffect(() => {
    const fetchData = async () => {
      const [response] = await getSupplierById();
      setSupplier(response?.data || []);
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      const [response] = await getProfile();
      if (response.status) {
        setProfile(response.data?.user);
      }
    };
    fetchProfile();
  }, []);

  const handleConfirmAction = async () => {
    if (modalConfig.actionType === "approve") {
      const [response] = await approveSupplier();
      if (response?.status) {
        showToast(response.message, true);
        window.location.reload();
      } else {
        showToast(response?.message, false);
      }
    } else if (modalConfig.actionType === "delete") {
      const [response] = await deleteSupplier();
      if (response?.status) {
        showToast(response.message, true);
        navigate("/dashboard/inventory");
      } else {
        showToast(response?.message, false);
      }
    }
  };

  const handleApprove = () => {
    setModalConfig({
      isVisible: true,
      actionType: "approve",
    });
  };

  const handleDelete = () => {
    setModalConfig({
      isVisible: true,
      actionType: "delete",
    });
  };

  function closeSupplierModal() {
    setSupplierModal(false);
  }

  function openSupplierModal() {
    setSupplierModal(true);
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar profile={profile as I_Profile} />

      <main className="flex-1 container mx-auto py-6">
      <div className="flex justify-between gap-5 mt-5 mx-10">
        <div className="flex gap-4">
          <div
            onClick={() => navigate(-1)}
            className="flex gap-2 font-semibold text-[16px] border-r-2 pr-4 border-separate cursor-pointer "
          >
            <Icon name="appbackIcon" className="mt-1" /> Back to Suppliers
          </div>

          <h1 className="md:text-[30px] -mt-2 font-bold">
            {capitalizeFirstLetter(supplier?.name)}
          </h1>
        </div>

        <div className="text-[14px] flex gap-2">
          <Button
            variant="secondary"
            onClick={openSupplierModal}
          >
            <Icon name="editIconBlack" /> Edit
          </Button>

          <Button
            type="button"
            variant="tertiary"
            onClick={handleDelete}
          >
            <Icon name="markIcon" /> Decline
          </Button>

          {supplier?.status === "pending" && (
            <Button
              type="button"
              variant="primary"
              
              onClick={handleApprove}
            >
              <Icon name="cancelIcon" /> Approve
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6 mt-5 mx-5 mb-20">
        <div className="col-span-2">
            <div className="flex flex-col justify-center items-center mx-10">
              <Tab
                activeTab={activeProduct}
                tabs={["Products", "Purchase Orders"]}
                setActiveTab={setActiveProduct}
              />
            </div>

            {activeProduct === "Products" && <h1>Products</h1>}
            {activeProduct === "Purchase Orders" && <h1>Purchase Order</h1>}
        </div>

        <div className="col-span-1 bg-white shadow-lg rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4 px-6 py-3">
            Supplier Details
          </h2>

          <div className="border-t pt-4 p-6 mt-5">
            <div className="flex flex-col gap-4">
              <div className="flex gap-4 items-center">
                  <div className="bg-[#E8F2E8] rounded-full p-3">
                      <Icon name="users" />
                  </div>
                  <div>
                      <p className="text-gray-500 text-sm mb-1">Supplier Id</p>
                      <p className="font-medium capitalize">{supplier?.supplierId}</p>
                  </div>
              </div>

              <div className="flex gap-4 items-center">
                <div className="bg-[#E8F2E8] rounded-full p-3">
                    <Icon name="users" />
                </div>
                <div>
                    <p className="text-gray-500 text-sm mb-1"> Name</p>
                    <p className="font-medium capitalize">{supplier?.name || "Nill"}</p>
                </div>
              </div>
              <div className="flex gap-4 items-center">
                <div className="bg-[#E8F2E8] rounded-full p-3">
                    <Icon name="users" />
                </div>
                <div>
                    <p className="text-gray-500 text-sm mb-1">Business Type</p>
                    <p className="font-medium capitalize">{supplier?.supplierType || "Nill"}</p>
                </div>
              </div>
              <div className="flex gap-4 items-center">
                <div className="bg-[#E8F2E8] rounded-full p-3">
                    <Icon name="users" />
                </div>
                <div>
                    <p className="text-gray-500 text-sm mb-1">Business Registration Number</p>
                    <p className="font-medium capitalize">{supplier?.businessRegNumber|| "Nill"}</p>
                </div>
              </div>
              <div className="flex gap-4 items-center">
                <div className="bg-[#E8F2E8] rounded-full p-3">
                    <Icon name="users" />
                </div>
                <div>
                    <p className="text-gray-500 text-sm mb-1">Contact Name</p>
                    <p className="font-medium capitalize">{supplier?.contactPerson|| "Nill"}</p>
                </div>
              </div>


            </div>

            

          </div>

        </div>
        
      </div>

      

      {loading ? (
        <RingLoader color="#ffffff" loading={loading} size={20} />
      ) : (
        <ActionModal
          visible={modalConfig.isVisible}
          onCancel={() => setModalConfig({ ...modalConfig, isVisible: false })}
          onConfirm={handleConfirmAction}
          title={`Product ${
            modalConfig.actionType === "approve" ? "Approval" : "Deletion"
          }`}
          content={`Are you sure you want to ${modalConfig.actionType} product creation?`}
          actionType={modalConfig.actionType}
        />
      )}
      </main>
      <AddSupplierDialog
        isVisible={supplierModal}
        onClose={closeSupplierModal}
        supplier={supplier}
      />
    </div>
  );
};

export default SupplierDetails;
